/**
 * @module virtual-collection/cart
 * Cart API logic — builds items[] payload and POSTs to /cart/add.js.
 */

import { SELECTORS, SOURCE } from './constants.js';
import { clearLoading, handleErrorMessage, setLoading } from './ui.js';

export type Variant = { id: number; available: boolean };

export type CartElement = {
  classList: Pick<DOMTokenList, 'contains' | 'remove'>;
  getSectionsToRender?: () => Array<{ id: string }>;
  renderContents?: (response: unknown) => void;
  setActiveElement?: (element: Element | null) => void;
};

type CartSections = Pick<CartElement, 'getSectionsToRender'>;

type CartResponse = {
  status?: unknown;
  description?: string;
  message?: string;
  errors?: unknown;
  [key: string]: unknown;
};

export type ShopifyError = Error & {
  shopifyError?: boolean;
  description?: string;
  errors?: unknown;
};

type AddItemsBody = {
  items: Array<{ id: number; quantity: number }>;
  sections?: string[];
  sections_url?: string;
};

/**
 * Adds multiple variants to the cart in a single request.
 * @param {Array<{id: number, available: boolean}>} variants - Variant data from the embedded JSON
 * @param {HTMLElement|null} cart - Cart drawer or notification element
 * @param {number} [quantity=1] - Quantity of each variant to add
 * @returns {Promise<object>} The Shopify cart response
 */
export async function addItemsToCart(
  variants: Variant[],
  cart: CartSections | null,
  quantity = 1,
): Promise<CartResponse> {
  const items = variants.filter((v) => v.available).map((v) => ({ id: v.id, quantity }));

  if (items.length === 0) {
    throw new Error('All items in this collection are currently unavailable.');
  }

  const body: AddItemsBody = { items };

  if (cart && typeof cart.getSectionsToRender === 'function') {
    body.sections = cart.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });

  let data: CartResponse;
  try {
    data = (await response.json()) as unknown as CartResponse;
  } catch {
    const error = new Error(
      'You have reached the maximum order quantity for this product',
    ) as ShopifyError;
    error.shopifyError = true;
    throw error;
  }

  if (!response.ok || data.status) {
    const error = new Error(
      data.description ||
        data.message ||
        'You have reached the maximum order quantity for this product',
    ) as ShopifyError;
    error.shopifyError = true;
    error.description = data.description;
    error.errors = data.errors;
    throw error;
  }

  return data;
}

/**
 * Publishes cart update or error events via the theme's pub/sub system.
 * @param {'update'|'error'} type
 * @param {object} detail
 */
export function publishCartEvent(type: 'update' | 'error', detail: Record<string, unknown>) {
  if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  if (type === 'update') {
    void publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, ...detail });
  } else if (type === 'error') {
    void publish(PUB_SUB_EVENTS.cartError, { source: SOURCE, ...detail });
  }
}

/**
 * Publishes the cart update and renders the cart drawer/notification, or redirects to /cart.
 * @param {HTMLElement|null} cart - Cart drawer or notification element
 * @param {object} response - The Shopify cart response
 */
export function handleCartSuccess(cart: CartElement | null, response: unknown) {
  publishCartEvent('update', { cartData: response });

  if (!cart) {
    (window as unknown as { location: string }).location = window.routes.cart_url;
    return;
  }

  if (cart.classList.contains('is-empty')) {
    cart.classList.remove('is-empty');
  }

  if (typeof cart.renderContents === 'function') {
    cart.renderContents(response);
  }
}

/**
 * Adds variants from `button` with its loading state, then renders the cart, or shows the failure
 * in the error wrapper inside `errorScope` after hiding every error in `root`.
 */
export async function addWithFeedback({
  button,
  root,
  errorScope,
  cart,
  variants,
  quantity = 1,
}: {
  button: HTMLElement;
  root: ParentNode;
  errorScope: ParentNode | null;
  cart: CartElement | null;
  variants: Variant[];
  quantity?: number;
}) {
  // aria-disabled, not disabled: disabling the focused button would drop focus to <body>.
  if (button.getAttribute('aria-disabled') === 'true') return;

  const spinner = button.querySelector<HTMLElement>(SELECTORS.spinner);
  root
    .querySelectorAll<HTMLElement>(SELECTORS.errorWrapper)
    .forEach((wrapper) => handleErrorMessage(wrapper, null));
  cart?.setActiveElement?.(document.activeElement);
  setLoading(button, spinner);

  try {
    handleCartSuccess(cart, await addItemsToCart(variants, cart, quantity));
  } catch (error) {
    const err = error as ShopifyError;
    handleErrorMessage(
      errorScope?.querySelector<HTMLElement>(SELECTORS.errorWrapper) ?? null,
      errorScope?.querySelector<HTMLElement>(SELECTORS.errorMessage) ?? null,
      // A network or script failure has no shopper-facing message of its own.
      (err.shopifyError && (err.description || err.message)) ||
        window.cartStrings?.error ||
        'Something went wrong. Please try again.',
    );
    if (err.shopifyError) {
      publishCartEvent('error', { errors: err.errors, message: err.description });
    }
  } finally {
    clearLoading(button, spinner);
  }
}
